// components/fridayDonation.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedCurrency } from '../../store/user';
import Summary from './summary';
// import DonationCalendar from './donationCalendar';
import { calculateFridayDonations } from '../../utils/fridayDonationUtils';
import { selectProducts } from '../../store/products';
import SelectCurrency from '../general/selectCurrency';
import SectionTitle from './sectionTitle';
import ProductSelector from '../recurringDonation/productSelector';
import TimeZoneSelector from '../recurringDonation/timeZoneSelector';
import 'react-datepicker/dist/react-datepicker.css';
import DateSelector from './dateSelector';
import { DateTime } from 'luxon';
// import DonationCalendarV2 from './donationCalendarV2';

const FridayDonationWidget = (props) => {
    const {
        productsList = [],
        processingTime = '12:00'
    } = props;

    const selectedCurrency = useSelector(selectSelectedCurrency);
    const productsFromStore = useSelector(selectProducts);

    const [allProducts, setAllProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // New state for "Start Date"
    const [startDate, setStartDate] = useState(null);   // ← start as null

    const [timeZone, setTimeZone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    const [donationSchedule, setDonationSchedule] = useState([]);
    const [summary, setSummary] = useState({
        totalAmount: 0,
        totalShares: 0,
        shareValue: 0,
    });

    const calendarRef = useRef(null);

    /* -------------------------------- Load products from the store -------------------------------- */
    useEffect(() => {
        if (productsFromStore && Object.keys(productsFromStore).length) {
            const temp = [];
            const tempDefaultProductsList = [
                'OPR-GEN-SUP-2023-01-01',
                'MD-AA-2024-02',
                'MKD-MN-001',
                'MD-AA-2024-07',
                'MKD-GEN-MZK-2023-01-123'
            ];
            Object.values(productsFromStore).forEach((category) => {
                if (category.sub_categories) {
                    category.sub_categories.forEach((subCat) => {
                        subCat.products.forEach((p) => {
                            // check if this product is in productsList
                            if (Number(p.status) === 1) {
                                if (productsList) {
                                    // Ensure CustomProductsList is always an array
                                    let tempCustomProducts = Array.isArray(productsList)
                                        ? productsList
                                        : productsList.split(',').map(item => item.trim()); // Convert string to array and trim spaces
                                    // Iterate through CustomProductsList to maintain the order
                                    tempCustomProducts.forEach((productCreator, __index) => {
                                        if (p.creator === productCreator) {
                                            temp[__index] = p;
                                        }
                                    });
                                } else {
                                    const matchedProduct = tempDefaultProductsList.find(
                                        (product) => product === p.creator
                                    );
                                    if (matchedProduct) {
                                        temp.push(p);
                                    }
                                }
                            }
                        });
                    });
                }
            });
            setAllProducts(temp);
        }
    }, [productsFromStore, productsList]);

    /* ------------------------------- Recalculate schedule on changes ------------------------------- */
    // useEffect(() => {
    //     if (selectedProducts.length === 0) {
    //         setDonationSchedule([]);
    //         setSummary({ totalAmount: 0, totalShares: 0, shareValue: 0 });
    //         return;
    //     }

    //     // Now we pass 'startDate' to the calculation
    //     const { donationSchedule: schedule, summary: newSummary } =
    //         calculateFridayDonations({
    //             startDate,          // <--- pass the new start date
    //             timeZone,
    //             selectedProducts,
    //             currency: selectedCurrency,
    //             splitOnDays: false, // or true, depending on your logic
    //             processingTime: processingTime
    //         });

    //     setDonationSchedule(schedule);
    //     setSummary(newSummary);
    // }, [startDate, timeZone, selectedProducts, selectedCurrency, processingTime]);

    useEffect(() => {
        if (selectedProducts.length === 0) {
            setDonationSchedule([]);
            setSummary({ totalAmount: 0, totalShares: 0, shareValue: 0 });
            return;
        }

        // Now we pass 'startDate' to the calculation
        const { donationSchedule: schedule, summary: newSummary } =
            calculateFridayDonations({
                startDate,          // <--- pass the new start date
                timeZone,
                selectedProducts,
                currency: selectedCurrency,
                splitOnDays: false, // or true, depending on your logic
                processingTime: processingTime
            });

        setDonationSchedule(schedule);
        setSummary(newSummary);
    }, [startDate, timeZone, selectedProducts, selectedCurrency, processingTime]);


    /* --------------------------- Load from localStorage on mount --------------------------- */
    useEffect(() => {
        const storedData = localStorage.getItem('FRIDAY_WIDGET_DATA');
        if (!storedData) return;

        const parsedData = JSON.parse(storedData);

        // if (parsedData.timeZone) {
        //     setTimeZone(parsedData.timeZone);
        // }
        if (parsedData.summary) {
            setSummary(parsedData.summary);
        }
        if (parsedData.donationSchedule) {
            setDonationSchedule(parsedData.donationSchedule);
        }
        // if (parsedData.startDate) {
        //     // console.log("start date 1=",new Date(parsedData.startDate))
        //     // setStartDate(new Date(parsedData.startDate));
        // }

        if (parsedData.selectedProducts && allProducts.length > 0) {
            const filtered = parsedData.selectedProducts.filter(saved =>
                allProducts.some(p => p.creator === saved.creator)
            );
            setSelectedProducts(filtered);
        }
    }, [allProducts]);  // ← now runs again once allProducts is populated

    const savedProductsRef = useRef([]);

    useEffect(() => {
        const stored = localStorage.getItem('FRIDAY_WIDGET_DATA');
        const now = DateTime.now().setZone(timeZone);
        const today = now.startOf('day');

        // Luxon: Monday=1 … Friday=5
        const daysUntilFriday = (12 - today.weekday) % 7; // 0‑6 days ahead
        let defaultFriday = today.plus({ days: daysUntilFriday });

        /* If that Friday is *today* and local time is past 11:30, pick next week */
        const isAfter1130AM = now.hour > 11 || (now.hour === 11 && now.minute >= 30);

        if (daysUntilFriday === 0 && isAfter1130AM) {
            defaultFriday = defaultFriday.plus({ days: 7 });
        }
        if (!stored) {
            setStartDate(defaultFriday.toJSDate());
            return
        };

        const parsed = JSON.parse(stored);
        if (parsed.timeZone) setTimeZone(parsed.timeZone);
        if (parsed.summary) setSummary(parsed.summary);
        if (parsed.donationSchedule) setDonationSchedule(parsed.donationSchedule);
        if (parsed.startDate) {
            // console.log("start date 2=",new Date(parsed.startDate))
            setStartDate(new Date(parsed.startDate));
        } else {
            setStartDate(defaultFriday.toJSDate());
        }

        // hold the raw array for later
        savedProductsRef.current = parsed.selectedProducts || [];
    }, []);

    // only once allProducts is ready, filter & restore
    useEffect(() => {
        if (allProducts.length === 0) return;

        const filtered = savedProductsRef.current.filter(saved =>
            allProducts.some(p => p.creator === saved.creator)
        );
        if (filtered.length > 0) {
            setSelectedProducts(filtered);
        }
    }, [allProducts]);

    // useEffect(() => {
    //     const storedData = localStorage.getItem('FRIDAY_WIDGET_DATA');
    //     if (storedData) {
    //         const parsedData = JSON.parse(storedData);

    //         if (parsedData.timeZone) {
    //             setTimeZone(parsedData.timeZone);
    //         }
    //         if (parsedData.selectedProducts) {
    //             const filtered = parsedData.selectedProducts.filter(saved =>
    //                 allProducts.some(p => p.creator === saved.creator)
    //             );
    //             setSelectedProducts(filtered);
    //             // setSelectedProducts(parsedData.selectedProducts);
    //         }
    //         if (parsedData.summary) {
    //             setSummary(parsedData.summary);
    //         }
    //         if (parsedData.donationSchedule) {
    //             setDonationSchedule(parsedData.donationSchedule);
    //         }

    //         // If the user had a saved startDate, we parse it back into a Date object
    //         if (parsedData.startDate) {
    //             setStartDate(new Date(parsedData.startDate));
    //         }
    //     }
    // }, []);

    /* --------------------------- Save to localStorage on each change --------------------------- */
    useEffect(() => {
        const dataToStore = {
            timeZone,
            selectedProducts,
            summary,
            donationSchedule,
            // Only call toISOString() if startDate is non-null:
            startDate: startDate ? startDate.toISOString() : null,
        };
        localStorage.setItem('FRIDAY_WIDGET_DATA', JSON.stringify(dataToStore));
    }, [timeZone, selectedProducts, summary, donationSchedule, startDate]);

    /* ---------- choose a default start date = next payable Friday ---------- */
    useEffect(() => {
        if (startDate) return;                            // user already picked
        if (!timeZone) return;                            // still unknown

        const now = DateTime.now().setZone(timeZone);
        const today = now.startOf('day');

        // Luxon: Monday=1 … Friday=5
        const daysUntilFriday = (12 - today.weekday) % 7; // 0‑6 days ahead
        let defaultFriday = today.plus({ days: daysUntilFriday });

        /* If that Friday is *today* and local time is past 11:30, pick next week */
        const isAfter1130AM = now.hour > 11 || (now.hour === 11 && now.minute >= 30);

        if (daysUntilFriday === 0 && isAfter1130AM) {
            defaultFriday = defaultFriday.plus({ days: 7 });
        }

        // setStartDate(defaultFriday.toJSDate());
    }, [timeZone, startDate]);

    return (
        <div className="w-full scrollarea min-h-[600px] font-inter">
            <div className="w-full py-2 md:py-4 flex flex-wrap items-start">
                {/* LEFT SIDE */}
                <div className="basis-full lg:basis-2/3 px-4 md:px-4 flex flex-col gap-4 md:gap-6">
                    <SectionTitle count={1} text="Select Start Date" />
                    {/* Start Date + TimeZone + Currency row */}
                    <div className="flex flex-wrap gap-4">
                        {/* Start Date */}
                        <h2 className="w-full text-stone-600">Choose the Friday that you’d like to start giving weekly. The earlier you start, the sooner you can make an impact and gain multiplied rewards and blessings.</h2>
                        <div className="w-[calc(50%-0.5rem)] md:w-auto md:flex-1">
                            <DateSelector
                                startDate={startDate}
                                setStartDate={setStartDate}
                                timeZone={timeZone}
                                label="Start Date"
                                enforceMinToday={true}
                            />
                        </div>

                        {/* Time Zone */}
                        <TimeZoneSelector timeZone={timeZone} setTimeZone={setTimeZone} classNames={'w-[calc(50%-0.5rem)] md:w-auto md:flex-1'} />

                        {/* Currency */}
                        <div className="w-full md:w-auto md:flex-1">
                            <label className="block text-stone-500 mb-1-">Currency</label>
                            <div
                                className="cursor-pointer relative w-full rounded-md h-12 border border-[#282828]"
                                id="ramadan-daily-currency"
                            >
                                <SelectCurrency
                                    onChangeCurrency={() => { }}
                                    className="h-full [&>div]:h-full-"
                                />
                            </div>
                        </div>
                    </div>

                    <SectionTitle count={2} text="Choose Your Causes" />
                    <h2 className="w-full text-stone-600">Select one or more causes to give to on your weekly Jummah giving.</h2>
                    <ProductSelector
                        products={allProducts}
                        selectedProducts={selectedProducts}
                        setSelectedProducts={setSelectedProducts}
                        currency={selectedCurrency}
                    />
                </div>

                {/* RIGHT SIDE - SUMMARY */}
                <div className="basis-full lg:basis-1/3 px-4 md:px-4 sticky top-28 mt-4 md:mt-0">
                    <Summary
                        selectedProducts={selectedProducts}
                        numberOfMultiplierNights={0}
                        numberOfNights={donationSchedule.filter((item) => item.shares === 1).length}
                        selectedStrategies={[
                            {
                                label: 'on Fridays',
                                multiplier: 1,
                                nights: donationSchedule.filter((d) => d.amount > 0).length,
                            },
                        ]}
                        totalAmount={summary.totalAmount}
                        currency={selectedCurrency}
                        viewCalendar={() =>
                            window.scrollTo({
                                behavior: 'smooth',
                                top: calendarRef.current.offsetTop - 100,
                            })
                        }
                        selectedConditions={[]}
                        donationOption="equal"
                        startDate={startDate}
                        isLastTenNightsGiving={false}
                    />
                </div>
            </div>

            {/* CALENDAR */}
            {/* <div
                className="w-full mt-4 py-2 md:py-4 px-0 md:px-4"
                id="calendar"
                ref={calendarRef}
            >
                <DonationCalendarV2
                    donationSchedule={donationSchedule}
                    currency={selectedCurrency}
                    ramadanStartDate="" // not used
                    timeZone={timeZone}
                    totalAmount={summary.totalAmount}
                    numberOfNights={donationSchedule.length}
                    isLastTenNightsGiving={false}
                    selectedProducts={selectedProducts}
                />
            </div> */}
        </div>
    );
};

export default FridayDonationWidget;
